import {
	isAnalyticsFlashCookieName,
	parseAnalyticsFlashEvents,
	toAnalyticsEventPayload,
} from '#app/utils/pz/analytics.flash'
import {
	captureAnalyticsAttribution,
	getAnalyticsAttributionProperties,
} from './analytics.attribution'
import {
	type AnalyticsPropertiesMap,
	type AnalyticsActionResult,
	type AnalyticsEventPayload,
	type AnalyticsIdentifyPayload,
	type AnalyticsPageViewPayload,
	analyticsSchemaVersion,
} from './analytics.catalog'

export type {
	AnalyticsActionResult,
	AnalyticsEventPayload,
	AnalyticsIdentifyPayload,
	AnalyticsPageViewPayload,
} from './analytics.catalog'
export { analyticsSchemaVersion } from './analytics.catalog'

declare global {
	interface Window {
		dataLayer: any[]
	}
}

export type AnalyticsContext = {
	userId?: string | null
	brand?: string
	productId?: string
	internalOrTestUser?: boolean
}

let runtimeAnalyticsContext: AnalyticsContext = {}

const billingContextKey = 'billing_context'
const signupSourceKey = 'signup_source'
const onboardingStartedAtKey = 'onboarding_started_at'

function readCookieValue(name: string): string | null {
	if (typeof document === 'undefined') return null
	const match = document.cookie
		.split('; ')
		.find((cookie) => cookie.startsWith(`${name}=`))
	return match ? (match.split('=')[1] ?? null) : null
}

function hasConsent(): boolean {
	if (typeof document === 'undefined') return false
	return readCookieValue('cookieConsent') === 'true'
}

export function hasAnalyticsConsent(): boolean {
	return hasConsent()
}

function isAnalyticsEnabled(): boolean {
	if (typeof window === 'undefined') return false
	return window.ENV?.ANALYTICS_ENABLED !== 'false'
}

function isAdminRoute(): boolean {
	if (typeof window === 'undefined') return false
	const path = window.location.pathname
	return path.startsWith('/admin') || path.startsWith('/app-admin')
}

function getContext(overrides?: AnalyticsContext): {
	brand: string
	productId?: string
	userId?: string | null
	internalOrTestUser: boolean
} {
	const hasUserIdOverride = Object.prototype.hasOwnProperty.call(
		overrides ?? {},
		'userId',
	)
	return {
		brand:
			overrides?.brand ??
			runtimeAnalyticsContext.brand ??
			window.ENV?.BRAND ??
			'unknown',
		productId:
			overrides?.productId ??
			runtimeAnalyticsContext.productId ??
			window.ENV?.PRODUCT_ID,
		userId: hasUserIdOverride
			? (overrides?.userId ?? null)
			: (runtimeAnalyticsContext.userId ?? null),
		internalOrTestUser:
			overrides?.internalOrTestUser ??
			runtimeAnalyticsContext.internalOrTestUser ??
			false,
	}
}

function getCommonEventProperties(internalOrTestUser: boolean): Record<
	string,
	string | boolean | undefined
> & {
	environment: string
	$internal_or_test_user: boolean
	referrer_host?: string
} {
	captureAnalyticsAttribution()
	return {
		environment: window.ENV?.MODE ?? 'unknown',
		$internal_or_test_user: internalOrTestUser,
		...getAnalyticsAttributionProperties(),
	}
}

/**
 * Sets request-derived analytics context before route components emit events.
 * The root app calls this during browser render so later effects cannot fall
 * back to a stale dataLayer user id.
 */
export function setAnalyticsContext(context: AnalyticsContext): void {
	runtimeAnalyticsContext = { ...context }
}

export function emitAnalyticsEvent(
	payload: AnalyticsEventPayload,
	context?: AnalyticsContext,
): void {
	if (typeof window === 'undefined') return
	if (!hasConsent()) return
	if (!isAnalyticsEnabled()) return
	if (isAdminRoute()) return

	window.dataLayer = window.dataLayer || []
	const { brand, productId, userId, internalOrTestUser } = getContext(context)
	const commonProperties = getCommonEventProperties(internalOrTestUser)

	window.dataLayer.push({
		event: 'analytics_event',
		schema_version: analyticsSchemaVersion,
		name: payload.name,
		brand,
		environment: commonProperties.environment,
		...(productId ? { product_id: productId } : {}),
		user_id: userId ?? null,
		$internal_or_test_user: internalOrTestUser,
		timestamp: new Date().toISOString(),
		properties: { ...commonProperties, ...(payload.properties ?? {}) },
	})
}

/**
 * Emits a typed event attached to a completed server action. This keeps
 * mutation events tied to persisted truth instead of optimistic UI clicks.
 */
export function emitAnalyticsActionResult(result: unknown): void {
	if (!result || typeof result !== 'object') return
	const event = (result as AnalyticsActionResult).analyticsEvent
	if (!event || typeof event.name !== 'string') return
	emitAnalyticsEvent(event)
}

export function emitPageView(
	payload: AnalyticsPageViewPayload,
	context?: AnalyticsContext,
): void {
	if (typeof window === 'undefined') return
	if (!hasConsent()) return
	if (!isAnalyticsEnabled()) return
	if (isAdminRoute()) return

	window.dataLayer = window.dataLayer || []
	const { brand, productId, userId, internalOrTestUser } = getContext(context)
	const commonProperties = getCommonEventProperties(internalOrTestUser)

	window.dataLayer.push({
		event: 'page_view',
		schema_version: analyticsSchemaVersion,
		brand,
		environment: commonProperties.environment,
		...(productId ? { product_id: productId } : {}),
		user_id: userId ?? null,
		$internal_or_test_user: internalOrTestUser,
		timestamp: new Date().toISOString(),
		page_type: payload.pageType,
		path: window.location.pathname,
		referrer_host: commonProperties.referrer_host,
		locale: document.documentElement.lang || undefined,
		properties: { ...commonProperties, ...(payload.properties ?? {}) },
	})
}

export function emitIdentify(
	payload: AnalyticsIdentifyPayload,
	context?: AnalyticsContext,
): void {
	if (typeof window === 'undefined') return
	if (!hasConsent()) return
	if (!isAnalyticsEnabled()) return
	if (isAdminRoute()) return

	window.dataLayer = window.dataLayer || []
	const { brand, productId, internalOrTestUser } = getContext({
		...context,
		userId: payload.userId,
	})

	window.dataLayer.push({
		event: 'identify',
		schema_version: analyticsSchemaVersion,
		user_id: payload.userId,
		// Compatibility alias for the current GTM container. Remove after the
		// container has migrated to the canonical snake_case variable.
		userId: payload.userId,
		brand,
		environment: window.ENV?.MODE ?? 'unknown',
		$internal_or_test_user: internalOrTestUser,
		...(productId ? { product_id: productId } : {}),
		timestamp: new Date().toISOString(),
		traits: {
			$internal_or_test_user: internalOrTestUser,
			...(payload.traits ?? {}),
		},
	})
}

export function emitResetIdentity(): void {
	if (typeof window === 'undefined') return
	if (!hasConsent()) return
	if (!isAnalyticsEnabled()) return
	if (isAdminRoute()) return

	window.dataLayer = window.dataLayer || []
	const { brand, productId, internalOrTestUser } = getContext({ userId: null })

	window.dataLayer.push({
		event: 'reset_identity',
		schema_version: analyticsSchemaVersion,
		user_id: null,
		// Compatibility alias; see emitIdentify.
		userId: null,
		brand,
		environment: window.ENV?.MODE ?? 'unknown',
		$internal_or_test_user: internalOrTestUser,
		...(productId ? { product_id: productId } : {}),
		timestamp: new Date().toISOString(),
	})
}

const deliveredFlashStorageKey = 'analytics_flash_delivered_v1'
const deliveredFlashLimit = 50
const flashLockName = 'pz-analytics-flash'

function readDeliveredFlashIds(): string[] {
	try {
		const parsed = JSON.parse(
			window.localStorage.getItem(deliveredFlashStorageKey) ?? '[]',
		) as unknown
		return Array.isArray(parsed)
			? parsed.filter((id): id is string => typeof id === 'string')
			: []
	} catch {
		return []
	}
}

function rememberDeliveredFlashIds(ids: string[]): void {
	try {
		window.localStorage.setItem(
			deliveredFlashStorageKey,
			JSON.stringify(ids.slice(-deliveredFlashLimit)),
		)
	} catch {
		// Storage can be full or blocked; the cleared cookie still prevents most repeats.
	}
}

function readFlashCookies(): Array<{ name: string; value: string }> {
	return document.cookie.split('; ').flatMap((cookie) => {
		const separator = cookie.indexOf('=')
		if (separator < 0) return []
		const name = cookie.slice(0, separator)
		return isAnalyticsFlashCookieName(name)
			? [{ name, value: cookie.slice(separator + 1) }]
			: []
	})
}

function clearFlashCookie(name: string): void {
	document.cookie = `${name}=;path=/;max-age=0;SameSite=Lax`
}

function canDeliverFlashEvents(): boolean {
	return hasConsent() && isAnalyticsEnabled() && !isAdminRoute()
}

function deliverPendingFlashEvents(context?: AnalyticsContext): void {
	// Checked again here: a lock request can wait while consent is withdrawn,
	// and nothing may be cleared or stored without it.
	if (!canDeliverFlashEvents()) return
	const cookies = readFlashCookies()
	if (cookies.length === 0) return
	// Claim first: a tab that reads the cookies after this finds nothing.
	for (const cookie of cookies) clearFlashCookie(cookie.name)
	const delivered = readDeliveredFlashIds()
	const deliveredIds = new Set(delivered)
	const fresh = cookies
		.flatMap((cookie) => parseAnalyticsFlashEvents(cookie.value))
		.filter((event) => !deliveredIds.has(event.id))
	if (fresh.length === 0) return
	rememberDeliveredFlashIds([...delivered, ...fresh.map((event) => event.id)])
	for (const event of fresh) {
		emitAnalyticsEvent(
			enrichEventWithClientContext(toAnalyticsEventPayload(event)),
			context,
		)
	}
}

/**
 * Delivers server-confirmed events once. Without consent the cookies wait, so
 * accepting analytics later in the visit still sends them. Tabs coordinate
 * through a Web Lock; browsers without Web Locks fall back to clearing first
 * and remembering delivered ids, which leaves a narrow two-tab race.
 */
export function emitAnalyticsFlashEvent(context?: AnalyticsContext): void {
	if (typeof document === 'undefined') return
	if (readFlashCookies().length === 0) return
	if (!canDeliverFlashEvents()) return

	const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined
	if (locks?.request) {
		void locks
			.request(flashLockName, () => deliverPendingFlashEvents(context))
			.catch(() => deliverPendingFlashEvents(context))
		return
	}
	deliverPendingFlashEvents(context)
}

export function clearAnalyticsFlashEvent(): void {
	if (typeof document === 'undefined') return
	for (const cookie of readFlashCookies()) clearFlashCookie(cookie.name)
}

export type BillingContext = {
	plan_id: string
	price: number
	currency: string
	billing_cycle?: string
}

export function setBillingContext(context: BillingContext): void {
	if (typeof window === 'undefined') return
	window.sessionStorage.setItem(billingContextKey, JSON.stringify(context))
}

export function getBillingContext(): BillingContext | null {
	if (typeof window === 'undefined') return null
	const value = window.sessionStorage.getItem(billingContextKey)
	if (!value) return null
	try {
		return JSON.parse(value) as BillingContext
	} catch {
		return null
	}
}

export function clearBillingContext(): void {
	if (typeof window === 'undefined') return
	window.sessionStorage.removeItem(billingContextKey)
}

export function setSignupSource(source: string | null): void {
	if (typeof window === 'undefined') return
	if (!source) return
	window.sessionStorage.setItem(signupSourceKey, source)
}

export function getSignupSource(): string | null {
	if (typeof window === 'undefined') return null
	return window.sessionStorage.getItem(signupSourceKey)
}

export function clearSignupSource(): void {
	if (typeof window === 'undefined') return
	window.sessionStorage.removeItem(signupSourceKey)
}

export function markOnboardingStarted(): void {
	if (typeof window === 'undefined') return
	window.sessionStorage.setItem(onboardingStartedAtKey, Date.now().toString())
}

export function consumeOnboardingDurationSeconds(): number | null {
	if (typeof window === 'undefined') return null
	const value = window.sessionStorage.getItem(onboardingStartedAtKey)
	if (!value) return null
	window.sessionStorage.removeItem(onboardingStartedAtKey)
	const startedAt = Number(value)
	if (Number.isNaN(startedAt)) return null
	return Math.max(0, Math.round((Date.now() - startedAt) / 1000))
}

function enrichEventWithClientContext(
	event: AnalyticsEventPayload,
): AnalyticsEventPayload {
	if (event.name === 'signup_completed') {
		const source = getSignupSource()
		const properties = event.properties as
			| AnalyticsPropertiesMap['signup_completed']
			| undefined
		if (source && !properties?.source) {
			clearSignupSource()
			return {
				...event,
				properties: { ...(properties ?? {}), source },
			}
		}
	}

	if (event.name === 'signup_verification_sent') {
		const source = getSignupSource()
		const properties = event.properties as
			| AnalyticsPropertiesMap['signup_verification_sent']
			| undefined
		if (source && !properties?.source) {
			return {
				...event,
				properties: { ...(properties ?? {}), source },
			}
		}
	}

	if (event.name === 'onboarding_completed') {
		const durationSeconds = consumeOnboardingDurationSeconds()
		if (durationSeconds != null) {
			return {
				...event,
				properties: {
					...(event.properties ?? {}),
					duration_seconds: durationSeconds,
				},
			}
		}
	}

	return event
}
